import { useMemo, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { App, Avatar, Select, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined, FlagFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TASK_PRIORITIES, type TaskActivityItem, type TaskItem, type TaskPriority } from '@crm/shared';
import { useUpdateTask } from '../../features/tasks/api';
import { useUsers } from '../../features/users/api';
import { dueLabel, isOverdue, PRIORITY_META } from '../../features/tasks/view';
import { errorMessage, fileUrl } from '../../api/client';
import { palette } from '../../app/theme';
import { fireConfetti } from '../../utils/confetti';
import { fmtDate } from '../../utils/format';

/**
 * Users tasks can be assigned to (active, plus invited ones who have not accepted yet; not disabled ones), and a
 * name lookup for optimistic updates.
 */
export function useTaskUsers() {
  const users = useUsers();
  return useMemo(() => {
    const list = (users.data ?? []).filter((u) => u.status !== 'DISABLED');
    return { list, names: new Map((users.data ?? []).map((u) => [u.id, u.name])) };
  }, [users.data]);
}

/** Ticks a task off (confetti!) or reopens it. */
export function useToggleComplete() {
  const { message } = App.useApp();
  const { names } = useTaskUsers();
  const update = useUpdateTask(names);
  return (task: Pick<TaskItem, 'id' | 'completedAt'>, e?: MouseEvent) => {
    const completed = !task.completedAt;
    if (completed) fireConfetti(e ? { x: e.clientX, y: e.clientY } : undefined);
    update.mutate({ id: task.id, completed }, { onError: (err) => message.error(errorMessage(err)) });
  };
}

export function CompleteToggle({ task, size = 18 }: { task: Pick<TaskItem, 'id' | 'title' | 'completedAt'>; size?: number }) {
  const toggle = useToggleComplete();
  const done = !!task.completedAt;
  return (
    <button
      type="button"
      className={`task-check${done ? ' task-check-done' : ''}`}
      style={{ width: size, height: size, fontSize: size * 0.6 }}
      aria-label={done ? `Mark "${task.title}" as not done` : `Mark "${task.title}" as done`}
      aria-pressed={done}
      onClick={(e) => {
        e.stopPropagation();
        toggle(task, e);
      }}
    >
      <CheckOutlined />
    </button>
  );
}

export function PriorityTag({ priority, compact }: { priority: TaskPriority | null; compact?: boolean }) {
  if (!priority) return null;
  const m = PRIORITY_META[priority];
  if (compact)
    return (
      <Tooltip title={`${m.label} priority`}>
        <FlagFilled style={{ color: m.color }} aria-label={`${m.label} priority`} />
      </Tooltip>
    );
  return (
    <span className="task-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
}

export const priorityOptions = TASK_PRIORITIES.slice()
  .reverse()
  .map((p) => ({
    value: p,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <FlagFilled style={{ color: PRIORITY_META[p].color }} />
        {PRIORITY_META[p].label}
      </span>
    ),
  }));

export function DueText({ task, empty }: { task: Pick<TaskItem, 'dueAt' | 'completedAt'>; empty?: ReactNode }) {
  if (!task.dueAt) return <>{empty ?? null}</>;
  const overdue = isOverdue(task);
  const today = !task.completedAt && dayjs(task.dueAt).isSame(dayjs(), 'day');
  return (
    <Tooltip title={fmtDate(task.dueAt)}>
      <span style={{ color: overdue ? palette.error : today ? palette.success : undefined, fontWeight: overdue || today ? 500 : undefined, whiteSpace: 'nowrap' }}>{dueLabel(task.dueAt)}</span>
    </Tooltip>
  );
}

const AVATAR_COLORS = ['#2F5BEA', '#12B76A', '#F79009', '#7A5AF8', '#E5484D', '#06AED4', '#DD2590', '#4E5BA6'];
const colorFor = (id: string) => AVATAR_COLORS[[...id].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

/** A user's profile photo when they set one (looked up in the cached user list), else coloured initials. */
export function UserAvatar({ user, size = 24 }: { user: { id: string; name: string; photoFileId?: string | null }; size?: number }) {
  const users = useUsers();
  const photoFileId = user.photoFileId !== undefined ? user.photoFileId : (users.data?.find((u) => u.id === user.id)?.photoFileId ?? null);
  return (
    <Avatar size={size} src={fileUrl(photoFileId)} style={{ background: colorFor(user.id), fontSize: Math.max(10, size * 0.42), flex: 'none' }}>
      {initials(user.name)}
    </Avatar>
  );
}

export function AssigneeCell({ user, showName = true }: { user: { id: string; name: string } | null; showName?: boolean }) {
  if (!user) return <Typography.Text type="secondary">—</Typography.Text>;
  const avatar = <UserAvatar user={user} size={22} />;
  return showName ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      {avatar}
      <Typography.Text ellipsis>{user.name}</Typography.Text>
    </span>
  ) : (
    <Tooltip title={user.name}>{avatar}</Tooltip>
  );
}

/** A user in a picker; invited users who have not accepted yet are marked. */
export function UserOption({ user }: { user: { id: string; name: string; status?: string } }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: '100%' }}>
      <AssigneeCell user={user} />
      {user.status === 'INVITED' && (
        <Tag style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '18px' }} title="Invited, has not accepted yet">
          Invited
        </Tag>
      )}
    </span>
  );
}

/** Assignee picker; null = unassigned. */
export function AssigneeSelect({ value, onChange, ...rest }: { value?: string | null; onChange?: (v: string | null) => void; style?: React.CSSProperties; size?: 'small' | 'middle'; variant?: 'borderless' | 'outlined' | 'filled'; placeholder?: string }) {
  const { list } = useTaskUsers();
  return (
    <Select
      allowClear
      showSearch={{ optionFilterProp: 'name' }}
      placeholder={rest.placeholder ?? 'No assignee'}
      value={value ?? undefined}
      onChange={(v) => onChange?.(v ?? null)}
      options={list.map((u) => ({ value: u.id, name: u.name, label: <UserOption user={u} /> }))}
      {...rest}
    />
  );
}

/** One line of task history: "Ada completed Write brief" (task named only when `withTask`). */
export function ActivityText({ item, withTask }: { item: TaskActivityItem; withTask?: boolean }) {
  const p = item.payload as Record<string, string | null | number | undefined>;
  const task = withTask ? (
    <Link to={`/tasks/${item.task.id}`} style={{ fontWeight: 500 }}>
      {item.task.title}
    </Link>
  ) : null;
  const on = (text: string) => (withTask ? <>{text} {task}</> : text);
  const b = (v: ReactNode) => <strong style={{ fontWeight: 500 }}>{v}</strong>;
  let text: ReactNode;
  switch (item.type) {
    case 'created':
      text = p.duplicateOf ? (
        <>{on('duplicated')} from #{p.duplicateOf}</>
      ) : p.importedFrom ? (
        <>imported {withTask ? task : 'this task'} from {p.importedFrom}</>
      ) : withTask ? (
        <>created {task}</>
      ) : (
        'created this task'
      );
      break;
    case 'completed':
      text = withTask ? <>completed {task}</> : 'marked this task complete';
      break;
    case 'reopened':
      text = withTask ? <>reopened {task}</> : 'marked this task incomplete';
      break;
    case 'renamed':
      text = <>renamed {withTask ? task : 'this task'} from {b(p.from)}</>;
      break;
    case 'assigned':
      text = p.to ? <>assigned {withTask ? task : 'this task'} to {b(p.to)}</> : <>unassigned {withTask ? task : 'this task'}</>;
      break;
    case 'due':
      text = p.to ? <>changed the due date{withTask && <> of {task}</>} to {b(fmtDate(String(p.to)))}</> : <>removed the due date{withTask && <> of {task}</>}</>;
      break;
    case 'priority':
      text = p.to ? <>set the priority{withTask && <> of {task}</>} to {b(p.to)}</> : <>cleared the priority{withTask && <> of {task}</>}</>;
      break;
    case 'section':
      text = <>moved {withTask ? task : 'this task'} to {b(p.to ?? 'No section')}</>;
      break;
    case 'project':
      text = <>moved {withTask ? task : 'this task'} to project {b(p.to ?? 'none')}</>;
      break;
    case 'description':
      text = <>updated the description{withTask && <> of {task}</>}</>;
      break;
    case 'parent':
      text = p.to ? <>made {withTask ? task : 'this task'} a subtask</> : <>made {withTask ? task : 'this task'} a top-level task</>;
      break;
    case 'subtask_added':
      text = <>added subtask {b(p.title)}{withTask && <> to {task}</>}</>;
      break;
    case 'comment':
      text = withTask ? <>commented on {task}</> : 'commented';
      break;
    case 'attachment_added':
      text = <>attached {b(p.fileName)}{withTask && <> to {task}</>}</>;
      break;
    case 'attachment_removed':
      text = <>removed the attachment {b(p.fileName)}{withTask && <> from {task}</>}</>;
      break;
    default:
      text = on('changed');
  }
  return (
    <span>
      {b(item.actor?.name ?? 'Someone')} {text}
    </span>
  );
}
